import { SOL_TOKEN_ADDRESS } from '@/config';
import { useAppEventBusContext } from '@/context/eventBusContext';
import { SubscribeEvent } from '@/interface/event';
import { useTradeStore } from '@/page/trade/store';
import { useBoomWallet } from '@boomeme/wallet-sdk';
import { useState } from 'react';
import BigNumber from 'bignumber.js';

interface IProps {
  amount: string;
  isTradeBuy: boolean;
}

export const useMarketTransaction = () => {
  const { tokenInfo, bscTradeSetting, solTradeSetting } = useTradeStore();
  const { transactions, evmTransactions, chain } = useBoomWallet();
  const [tradeLoading, setTradeLoading] = useState(false);
  const { publish } = useAppEventBusContext();

  const targetTradeSetting = chain == 'solana' ? solTradeSetting : bscTradeSetting;
  const { slippage, slippageAuto, fee, feeAuto } = targetTradeSetting;

  const handleSolanaTransaction = async (props: IProps) => {
    try {
      if (!tokenInfo?.address) return;
      setTradeLoading(true);
      const { address, decimals, dexType, isCompleted } = tokenInfo;
      const { amount, isTradeBuy } = props;
      const tokenDecimals = isTradeBuy ? 9 : (decimals as number);
      const [inputTokenAddress, outputTokenAddress] = isTradeBuy ? [SOL_TOKEN_ADDRESS, address || ''] : [address || '', SOL_TOKEN_ADDRESS];
      const tradeType = dexType === 1 ? (isCompleted ? 'jup' : 'pump') : 'jup';
      const amountIn = BigNumber(amount)
        .times(10 ** tokenDecimals)
        .toFixed(0);
      const hash = await transactions.trade({
        inputTokenAddress,
        outputTokenAddress,
        tradeType,
        amountIn,
        ...(!feeAuto && { priorityFee: BigNumber(fee).times(1e9).toString() }),
        ...(!slippageAuto && { slippage: Math.round(Number(slippage) * 100) }),
      });
      return hash ? true : false;
    } catch (error) {
      throw error;
    } finally {
      setTradeLoading(false);
    }
  };

  const handleBscTransaction = async (props: IProps) => {
    try {
      if (!tokenInfo?.address) return;
      setTradeLoading(true);
      const { amount, isTradeBuy } = props;
      const { address, decimals, dexType, symbol, isCompleted } = tokenInfo;
      const tradeType = dexType === 1 ? (isCompleted ? 'pancake' : 'fourmeme') : 'pancake';
      const isPumpFromFourmeme = dexType == 1 ? true : false;
      if (isTradeBuy) {
        const hash = await evmTransactions?.buy?.({
          outputToken: {
            outputTokenAddress: address,
            outputTokenDecimals: decimals,
            outputTokenName: symbol,
          },
          inputTokenAmount: Number(amount),
          tradeType,
          isPumpFromFourmeme,
          ...(!slippageAuto && { slippage: Number(slippage) * 100 }),
          ...(!feeAuto && { gasFee: (Number(fee) * 1e9).toString() }),
        });
        if (hash) {
          publish(SubscribeEvent.Update_Balance);
          return true;
        } else {
          return false;
        }
      } else {
        const params = {
          inputToken: {
            inputTokenAddress: address,
            inputTokenDecimals: decimals,
            inputTokenName: symbol,
          },
          inputTokenAmount: BigNumber(amount).toFixed(10, BigNumber.ROUND_DOWN),
          isPumpFromFourmeme,
          tradeType,
          ...(!slippageAuto && { slippage: Number(slippage) * 100 }),
          ...(!feeAuto && { gasFee: (Number(fee) * 1e9).toString() }),
        };
        const hash = await evmTransactions?.sell?.(params as any);
        if (hash) {
          publish(SubscribeEvent.Update_Balance);
          return true;
        } else {
          return false;
        }
      }
    } catch (error) {
      throw error;
    } finally {
      setTradeLoading(false);
    }
  };

  const submitTransaction = async (props: IProps) => {
    if (chain == 'solana') {
      return await handleSolanaTransaction(props);
    } else {
      return await handleBscTransaction(props);
    }
  };

  return {
    submitTransaction,
    tradeLoading,
  };
};
